import React from 'react'
import { createParam } from 'solito'

import { useQuery } from '@apollo/client'
import { PageLoading } from '@bounty/creators-design-system'
import { UnreachableCaseError } from '@bounty/utils'

import { RequireUpdatedTermsModal } from '../../../components/RequireUpdatedTerms/RequireUpdatedTermsModal'
import { RequireVerifiedInstagram } from '../../../components/RequireVerifiedInstagram/RequireVerifiedInstagram'
import { RequireVerifiedTikTok } from '../../../components/RequireVerifiedTikTok/RequireVerifiedTikTok'
import { RequireWelcomeModal } from '../../../components/RequireWelcomeFlow/RequireWelcomeModal'
import {
  BountyDetailDocument,
  BountyDetailQuery,
  UserBountyContentType,
} from '../../../generated/backendGraphql'
import { BountyDetailAccepted } from './components/BountyDetail.Accepted'
import { BountyDetailApproved } from './components/BountyDetail.Approved'
import { BountyDetailError } from './components/BountyDetail.Error'
import { BountyDetailLive } from './components/BountyDetail.Live'
import { BountyDetailPending } from './components/BountyDetail.Pending'
import { BountyDetailRejected } from './components/BountyDetail.Rejected'
import { BountyDetailSatisfied } from './components/BountyDetail.Satisfied'

const { useParam } = createParam()

const withRequireVerifiedSocial = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  return (
    props: P & {
      bountyId: string
      contentTypeSubmitted?: UserBountyContentType | null
    },
  ) => {
    const { bountyId, contentTypeSubmitted } = props

    if (contentTypeSubmitted === 'TIKTOK_VIDEO') {
      return (
        <RequireVerifiedTikTok
          redirectToOnAuthSuccess={`/bounties/${bountyId}`}
        >
          <Component {...props} />
        </RequireVerifiedTikTok>
      )
    }

    if (contentTypeSubmitted === 'INSTAGRAM_REEL') {
      return (
        <RequireVerifiedInstagram
          redirectToOnAuthSuccess={`/bounties/${bountyId}`}
        >
          <Component {...props} />
        </RequireVerifiedInstagram>
      )
    }

    return <Component {...props} />
  }
}

function BountyDetail({ data }: { data: BountyDetailQuery }) {
  switch (data.bounty.status) {
    case 'PENDING':
      return <BountyDetailPending data={data} />
    case 'ACCEPTED':
      return <BountyDetailAccepted data={data} />
    case 'APPROVED':
      return <BountyDetailApproved data={data} />
    case 'LIVE':
      return <BountyDetailLive data={data} />
    case 'HOLD':
    case 'SATISFIED':
      return <BountyDetailSatisfied data={data} />
    case 'REJECTED':
      return <BountyDetailRejected data={data} />
    case 'ERROR':
      return <BountyDetailError data={data} />
    default:
      throw new UnreachableCaseError(data.bounty.status)
  }
}

const RequireVerifiedSocialBountyDetail =
  withRequireVerifiedSocial(BountyDetail)

export function BountyDetailScreen() {
  const [id] = useParam('id')

  if (!id) {
    throw new Error(`Cannot render Bounty without id param.`)
  }

  const { data, loading } = useQuery(BountyDetailDocument, {
    variables: { bountyId: id },
    fetchPolicy: 'network-only',
  })

  if (loading || !data) return <PageLoading />

  return (
    <RequireWelcomeModal>
      <RequireUpdatedTermsModal>
        <RequireVerifiedSocialBountyDetail
          data={data}
          bountyId={data.bounty.id}
          contentTypeSubmitted={data.bounty.contentTypeSubmitted}
        />
      </RequireUpdatedTermsModal>
    </RequireWelcomeModal>
  )
}
